import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { GraduationApi, GraduateExportCategory, errorMessage } from '../services/GraduationApi';
import { containModalFocus } from './modalFocus';

export default function GraduateApplicationsExportDialog({ categories, statusId, onClose }: {
  categories: (GraduateExportCategory & { label: string })[];
  statusId: number | null;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState(categories.map(category => category.applicationType));
  const [currentFilters, setCurrentFilters] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);

  const download = async (event: React.FormEvent) => {
    event.preventDefault();
    if (pending.current || selected.length === 0) return;
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true); setError('');
    try {
      const blob = await GraduationApi.graduateApplicationsExport({
        categories: categories.filter(category => selected.includes(category.applicationType))
          .map(({ applicationType, identifier, sort, direction }) => ({ applicationType, identifier, sort, direction })),
        statusId, currentFilters,
      }, controller.signal);
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Graduate-Applications-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      try { link.click(); } finally {
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      onClose();
    } catch (e) {
      if (!controller.signal.aborted && !axios.isCancel(e)) setError(errorMessage(e));
    } finally {
      if (!controller.signal.aborted) { pending.current = null; setBusy(false); }
    }
  };

  return <Modal show onHide={onClose} onKeyDown={containModalFocus} aria-labelledby="graduate-export-heading" centered>
    <Form onSubmit={download}>
      <Modal.Header closeButton><Modal.Title id="graduate-export-heading">Export Graduate Applications</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        <fieldset disabled={busy} className="mb-3">
          <legend className="h6">Categories to export</legend>
          <Button variant="outline-secondary" size="sm" className="mb-2" onClick={() => setSelected(categories.map(category => category.applicationType))}>Select all categories</Button>
          {categories.map(category => <Form.Check key={category.applicationType} type="checkbox"
            id={`graduate-export-category-${category.applicationType}`} label={category.label}
            checked={selected.includes(category.applicationType)} onChange={event => setSelected(previous => event.target.checked
              ? [...previous, category.applicationType] : previous.filter(type => type !== category.applicationType))} />)}
        </fieldset>
        <Form.Group controlId="graduate-export-scope" className="mb-3">
          <Form.Label>Applications to include</Form.Label>
          <Form.Select disabled={busy} value={currentFilters ? 'filtered' : 'all'} onChange={event => setCurrentFilters(event.target.value === 'filtered')}>
            <option value="filtered">Current table filters (all matching rows)</option>
            <option value="all">All applications in selected categories, including archived</option>
          </Form.Select>
        </Form.Group>
        <p className="mb-0 text-secondary">The Applications worksheet contains one row per application, including its details and statuses, all capstones, faculty approvals, and shared and admin-only notes. Exports include all matching rows, regardless of page size.</p>
        {busy && <p role="status" className="mt-3 mb-0">Preparing Excel export…</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={busy || selected.length === 0}>{busy ? 'Exporting…' : 'Download Excel'}</Button>
      </Modal.Footer>
    </Form>
  </Modal>;
}
